import React, { useState, useEffect } from 'react'
import Pusher from 'pusher-js'
import { useApolloClient, gql } from '@apollo/client'
import { useAccountContext } from '~/modules/account/context'

import { useCurrentChannel } from '~/app/state/hooks'
import { usePrevious } from '~/shared/hooks'
import { useDismissAll } from '~/modules/post/hooks'

import {
  useEventBackfillDone,
  useEventCountersUpdated,
} from '~/modules/realtime/hooks'
import { env } from '~/env'

/**
 *
 */
export const CHANNELS = {
  ORGANIZATION: 'organization',
  CHANNEL: 'channel',
}

export const EVENTS = {
  CHANNEL_BACKFILL_DONE: 'channel-backfilling-done',
  POST_DISMISS_ALL_DONE: 'post-dismissing-done',
  COUNTERS_UPDATED: 'counters-update',
}

export const PUSHER_AUTHENTICATION_QUERY = gql`
  query engagePusherAuthentication(
    $socketId: String!
    $pusherChannel: String!
    $organizationId: String!
  ) {
    engagePusherAuthentication(
      socketId: $socketId
      pusherChannel: $pusherChannel
      organizationId: $organizationId
    ) {
      auth
    }
  }
`

/**
 *
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'client' implicitly has an 'any' type.
function initPusher(client) {
  Pusher.logToConsole = false

  const pusher = new Pusher(env.PUSHER_KEY, {
    cluster: env.PUSHER_CLUSTER,
    forceTLS: true,
    authorizer: ({ name }) => ({
      authorize: async (socketId, callback) => {
        const result = await client.query({
          query: PUSHER_AUTHENTICATION_QUERY,
          variables: { socketId, pusherChannel: name, organizationId: '' },
        })

        const { data } = result || {}
        const { engagePusherAuthentication } = data || {}
        const { auth } = engagePusherAuthentication || {}

        if (auth) {
          callback(false, { auth })
        } else {
          // @ts-expect-error TS(2554) FIXME: Expected 2 arguments, but got 1.
          callback(true)
        }
      },
    }),
  })

  if (!pusher) {
    console.error('[withRealtime] Pusher could not be initialized.')
  }

  return pusher
}

/**
 *
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'pusher' implicitly has an 'any' type.
function subscribe(pusher, channelName) {
  const channel = pusher.subscribe(channelName)

  if (!channel) {
    console.error(
      `[withRealtime] Subscription to channel ${channelName} failed.`,
    )
  }

  return channel
}

/**
 *
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'Component' implicitly has an 'any' type... Remove this comment to see the full error message
const withRealtime = (Component) => (props) => {
  const [pusher, setPusher] = useState(null)

  const [organizationEvents, setOrganizationEvents] = useState(null)
  const [channelEvents, setChannelEvents] = useState(null)

  const client = useApolloClient()

  const account = useAccountContext()

  const onEventBackfillDone = useEventBackfillDone()
  const onEventCountersUpdated = useEventCountersUpdated()
  const { dismissAllDone } = useDismissAll()

  // TODO: All of this could be replaced by saving:
  // - Selected Organization.
  // - Selected Channel.
  // on an application context.
  const { currentChannel, queryCurrentChannel } = useCurrentChannel()

  useEffect(() => {
    queryCurrentChannel()

    const _pusher = initPusher(client)
    if (_pusher) {
      // @ts-expect-error TS(2345) FIXME: Argument of type 'Pusher' is not assignable to par... Remove this comment to see the full error message
      setPusher(_pusher)
    }
  }, [])

  useEffect(() => {
    // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
    if (!account?.id || !pusher) {
      return
    }

    // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
    const organization = account.currentOrganization
    if (!organization?.id) {
      return
    }

    const result = subscribe(
      pusher,
      `${CHANNELS.ORGANIZATION}-${organization.id}`,
    )
    result && setOrganizationEvents(result)
  }, [account, pusher])

  const prevChannel = usePrevious(currentChannel)
  useEffect(() => {
    if (!currentChannel || !pusher) {
      return
    }

    // @ts-expect-error TS(2339) FIXME: Property 'serviceId' does not exist on type 'never... Remove this comment to see the full error message
    const sameChannel = currentChannel.serviceId === prevChannel?.serviceId
    if (sameChannel) {
      return
    }

    // @ts-expect-error TS(2339) FIXME: Property 'serviceId' does not exist on type 'never... Remove this comment to see the full error message
    if (prevChannel?.serviceId) {
      // @ts-expect-error TS(2339) FIXME: Property 'unsubscribe' does not exist on type 'nev... Remove this comment to see the full error message
      pusher.unsubscribe(`${CHANNELS.CHANNEL}-${prevChannel.serviceId}`)
    }

    const result = subscribe(
      pusher,
      `${CHANNELS.CHANNEL}-${currentChannel.serviceId}`,
    )
    result && setChannelEvents(result)
  }, [currentChannel, pusher])

  const prevOnEventBackfillDone = usePrevious(onEventBackfillDone)
  useEffect(() => {
    if (!organizationEvents) {
      return
    }

    if (prevOnEventBackfillDone) {
      // @ts-expect-error TS(2339) FIXME: Property 'unbind' does not exist on type 'never'.
      organizationEvents.unbind(
        EVENTS.CHANNEL_BACKFILL_DONE,
        prevOnEventBackfillDone,
      )
    }

    // @ts-expect-error TS(2339) FIXME: Property 'bind' does not exist on type 'never'.
    organizationEvents.bind(EVENTS.CHANNEL_BACKFILL_DONE, onEventBackfillDone)
    // @ts-expect-error TS(2339) FIXME: Property 'bind' does not exist on type 'never'.
    organizationEvents.bind(EVENTS.POST_DISMISS_ALL_DONE, dismissAllDone)
  }, [organizationEvents, onEventBackfillDone])

  const prevEventCountersUpdated = usePrevious(onEventCountersUpdated)
  useEffect(() => {
    if (!channelEvents) {
      return
    }

    if (prevEventCountersUpdated) {
      // @ts-expect-error TS(2339) FIXME: Property 'unbind' does not exist on type 'never'.
      channelEvents.unbind(EVENTS.COUNTERS_UPDATED, prevEventCountersUpdated)
    }

    // @ts-expect-error TS(2339) FIXME: Property 'bind' does not exist on type 'never'.
    channelEvents.bind(EVENTS.COUNTERS_UPDATED, onEventCountersUpdated)
  }, [channelEvents, onEventCountersUpdated])

  return <Component {...props} />
}

export default withRealtime
